import { withSentryReactRouterV6Routing } from "@sentry/react";
import { Suspense, useMemo } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import MobileHistoricalPage from "../pages/HistoricalPage/mobile/MobileHistoricalPage";
import MobileRFQPage from "../pages/RFQPage/mobile/MobileRFQPage";
import { ReferralRedirectPage } from "../pages/ReferralRedirectPage";
import MobileReferralsPage from "../pages/ReferralsPage/mobile";
import TradingPage from "../pages/TradingPage";
import MobileTradingPage from "../pages/TradingPage/MobileTradingPage";
import { InstrumentTypeResponse } from "../codegen-api";
import { isInstrumentTypeEnabled } from "../config/features";
import { PageEndpointEnum } from "../enums/endpoint";
import { getReferralRewardsHistoryPath } from "../pages/ReferralsTab/referralTabs";
import useScreenSize from "../hooks/screenSize/useScreenSize";
import { IRoute } from "./shared";
import { validTradingRoutes } from "./tradingRoute";
import { OutletRoute } from "./routes.outlet";
import {
  RFQPage,
  HistoricalPage,
  PortfolioPage,
  ReferralsPage,
  LeaderboardPage,
  RewardsPage,
  MobilePortfolioPage,
  SettingsPage,
  MobileSettingsPage,
  WatchlistPage,
  StatsPage,
  ShortcutsPage,
} from "./routes.lazy";

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

function PerpsRoutes() {
  const { isMobileScreen } = useScreenSize();

  // Any derivatives vertical (perps or options) enables the RFQ surface.
  const derivativesEnabled =
    isInstrumentTypeEnabled(InstrumentTypeResponse.Perpetual) ||
    isInstrumentTypeEnabled(InstrumentTypeResponse.Option);

  const routes = useMemo(() => {
    const tradingPage = isMobileScreen ? (
      <MobileTradingPage />
    ) : (
      <TradingPage />
    );
    const tradingRoutes = validTradingRoutes.map((r) => ({
      path: r,
      page: tradingPage,
    }));
    const allRoutes: IRoute[] = [
      {
        path: `${PageEndpointEnum.PORTFOLIO}/*`,
        page: isMobileScreen ? (
          <Suspense>
            <MobilePortfolioPage />
          </Suspense>
        ) : (
          <Suspense>
            <PortfolioPage />
          </Suspense>
        ),
      },
      {
        path: `${PageEndpointEnum.REFERRALS}/*`,
        page: isMobileScreen ? (
          <Suspense>
            <MobileReferralsPage />
          </Suspense>
        ) : (
          <Suspense>
            <ReferralsPage />
          </Suspense>
        ),
      },
      {
        path: "/referral-rewards-history",
        page: <Navigate to={getReferralRewardsHistoryPath()} replace />,
      },
      {
        path: `${PageEndpointEnum.LEADERBOARD}`,
        page: (
          <Suspense>
            <LeaderboardPage />
          </Suspense>
        ),
      },
      {
        path: `${PageEndpointEnum.STATS}`,
        page: (
          <Suspense>
            <StatsPage />
          </Suspense>
        ),
      },
      {
        path: `${PageEndpointEnum.MARGINX}/*`,
        page: isMobileScreen ? (
          <Suspense>
            <RewardsPage />
          </Suspense>
        ) : (
          <Suspense>
            <RewardsPage />
          </Suspense>
        ),
      },
      {
        path: `${PageEndpointEnum.HISTORICAL}/*`,
        page: isMobileScreen ? (
          <Suspense>
            <MobileHistoricalPage />
          </Suspense>
        ) : (
          <Suspense>
            <HistoricalPage />
          </Suspense>
        ),
      },
      // RFQ builds multi-leg option/perp strategies, so it needs at least one
      // derivatives vertical. On a spot-only build there is nothing to quote —
      // keep the route registered so the URL resolves, but redirect to trading
      // (same shape as the FAUCET redirect).
      {
        path: `${PageEndpointEnum.RFQ}/*`,
        page: !derivativesEnabled ? (
          <Navigate to={PageEndpointEnum.TRADING} replace />
        ) : isMobileScreen ? (
          <Suspense>
            <MobileRFQPage />
          </Suspense>
        ) : (
          <Suspense>
            <RFQPage />
          </Suspense>
        ),
      },
      // todo: readd later
      // {
      //   path: `${PageEndpointEnum.STRATEGIES}/*`,
      //   page: (
      //     <Suspense>
      //       <StrategiesPage isMobile={isMobileScreen} />
      //     </Suspense>
      //   ),
      // },
      {
        path: `${PageEndpointEnum.SETTINGS}/*`,
        page: isMobileScreen ? (
          <Suspense>
            <MobileSettingsPage />
          </Suspense>
        ) : (
          <Suspense>
            <SettingsPage />
          </Suspense>
        ),
      },
      {
        path: `${PageEndpointEnum.REF_LINK}/*`,
        page: <ReferralRedirectPage />,
      },
      {
        path: `${PageEndpointEnum.FAUCET}`,
        page: <Navigate to={PageEndpointEnum.TRADING} replace />,
      },
      {
        path: `${PageEndpointEnum.WATCHLIST}`,
        page: (
          <Suspense>
            <WatchlistPage />
          </Suspense>
        ),
      },
      {
        path: `${PageEndpointEnum.PWA}`,
        page: tradingPage,
      },
      {
        path: "/shortcuts",
        page: (
          <Suspense>
            <ShortcutsPage />
          </Suspense>
        ),
      },
      // Trading routes.
      // Supports url like
      // marginx.trade/option/eth, marginx.trade/perpetual/eth, marginx.trade/spot/btc
      ...tradingRoutes,
      // Catch-all. The trading patterns cover up to three segments, so a
      // deeper or otherwise unmatched URL previously rendered the shell with
      // an empty Outlet — a blank page with no way back. Send it to trading,
      // which then normalises to the default vertical.
      {
        path: "*",
        page: <Navigate to={PageEndpointEnum.TRADING} replace />,
      },
    ];
    return allRoutes;
  }, [isMobileScreen, derivativesEnabled]);

  return (
    <SentryRoutes>
      <Route key={"/"} path={"/"} element={<OutletRoute />}>
        {routes.map((route) => (
          <Route key={route.path} path={route.path} element={route.page} />
        ))}
      </Route>
    </SentryRoutes>
  );
}

export default PerpsRoutes;
